// @flow
/* eslint-disable ta-code-conventions/has-matching-test, ta-code-conventions/has-matching-storydoc */
import * as React from 'react';
import { Query } from 'react-apollo';
import { connect } from 'react-redux';

// absolute imports
import type { ViewportCategory } from '@ta/common.state';

// relative imports
import SponsoredHomeQuery from './SponsoredHomeQuery.gql';
import type { Sponsorship, HomepageHeroServlet, HomepageHeroPlatform } from './types';

type ExternalProps = {|
  servlet?: HomepageHeroServlet,
  sponsorship?: Sponsorship,
  children: (sponsorship?: Sponsorship, loading?: boolean) => React.Node,
|};

type Props = {
  locationId: number,
  puid: string | null,
  uniqueId: string | null,
  sessionId: string | null,
  viewportCategory: ViewportCategory,
  locale: string,
  ...ExternalProps,
};

/**
 * Is this a valid sponsorship (profile with photo)
 * @param sponsorship
 */
export const isValidSponsorship = (sponsorship?: Sponsorship): boolean => {
  if (!sponsorship) {
    return false;
  }
  const { profile, photo, video } = sponsorship;

  // must have profile
  if (!profile) {
    console.error('Sponsored homepage hero missing sponsored profile');
    return false;
  }
  if (!profile.username) {
    console.error("Sponsored homepage hero missing sponsored profile's username");
    return false;
  }

  // must have photo or video
  if (photo && photo.photoSizes) {
    return true;
  }
  if (video && video.sources) {
    if (!video.posterSizes) {
      console.error('Sponsored homepage hero missing video poster');
      return false;
    }
    return true;
  }
  console.error('Sponsored homepage hero missing photo or video');
  return false;
};

function getPlatformFromViewportCategory(viewportCategory: ViewportCategory): HomepageHeroPlatform {
  switch (viewportCategory.toLowerCase()) {
    case 'mobile':
      return 'MOBILE_WEB';
    default:
      return 'DESKTOP';
  }
}

const SponsoredHomeProvider = ({
  servlet,
  sponsorship,
  locationId,
  puid,
  uniqueId,
  sessionId,
  viewportCategory,
  locale,
  children,
}: Props) => {
  /** TODO: ADS-5201 - Make /Home use GraphQL once we've moved off of HomepageHeroNodeRenderer.java
   *  If props have a sponsorship. (ie from the node renderer) use it else make a gql query.
   *  Note: Currently all the sponsorships coming from HomepageHeroNodeRenderer.java are for the Home so if you want to query for a specific servlet use the gql.
   */
  const platform = getPlatformFromViewportCategory(viewportCategory);
  // Used to manually override for testing on dev
  const date = new Date(Date.now()).toISOString().slice(0, 10);
  const geoId = locationId || 1;
  const tracking = { sessionId, uniqueId, puid };
  const pageServlet = servlet || 'HOME';

  if (sponsorship && isValidSponsorship(sponsorship)) {
    return children(sponsorship, false);
  }
  return (
    <React.Fragment>
      <Query
        query={SponsoredHomeQuery}
        variables={{
          geo: geoId,
          locale,
          servlet: pageServlet,
          date,
          platform,
          tracking,
        }}
      >
        {({ data, loading }) => {
          let sponsorshipData;

          if (data && data.sponsoredHome && isValidSponsorship((data.sponsoredHome: any))) {
            sponsorshipData = data.sponsoredHome;
          }

          return children((sponsorshipData: any), loading);
        }}
      </Query>
    </React.Fragment>
  );
};

SponsoredHomeProvider.defaultProps = {
  sponsorship: undefined,
  photo: undefined,
  servlet: 'HOME',
  showGeoPill: false,
};

export default connect<Props, ExternalProps, _, _, _, _>((state, props: ExternalProps) => ({
  ...props,
  locationId: state.route.geo,
  puid: state.tracking.uid,
  uniqueId: state.tracking.uniqueId,
  sessionId: state.tracking.sessionId,
  locale: state.i18n.locale,
  viewportCategory: state.meta.device.viewportCategory,
}))(SponsoredHomeProvider);
